import T from 'prop-types'
import Joi from 'joi'
import { formatDateClean } from 'lib/date'
import { useHistory } from 'react-router-dom'
import { uppercaseFirst } from 'lib/utils'
import Icon from 'components/Icon'
import Step from 'apps/booking/components/Step'
import Button from 'components/Button'
import Editorial from 'components/Editorial'
import { filterActiveVaccinations, filterFutureVaccinations } from 'lib/filterBookingVaccinations'
import calculateAllowDose3BookingFromDate from 'apps/booking/helpers/calculateAllowDose3BookingFromDate'

export const validator = (form) => {
  const hasActiveVaccinations = !!filterActiveVaccinations(form?.registeredVaccinations)?.length
  const hasFutureVaccinations = !!filterFutureVaccinations(form?.registeredVaccinations)?.length

  // This step should be validated when:
  // User has active vaccinations or in the future (and then you're only allowed to pass if selecting an active vaccination)
  const performValidation = hasFutureVaccinations || hasActiveVaccinations

  return Joi.object({
    ...(performValidation
      ? {
          vaccinationId: Joi.number().allow(null).required(),
        }
      : {}),
  }).unknown(true)
}

// Show this step if user has vaccinations in the future
export const vaccinationsPredicate = (values) => {
  // Skip this step if booking on behalf of another person
  if (values?.details?.on_behalf_of?.national_id) {
    return false
  }

  const hasActiveVaccinations = !!filterActiveVaccinations(values?.registeredVaccinations)?.length
  const hasFutureVaccinations = !!filterFutureVaccinations(values?.registeredVaccinations)?.length

  if (hasActiveVaccinations || hasFutureVaccinations) {
    return true
  }

  return false
}

export default function Vaccinations({ changeStep, onChange, values, orgUnit, bookingType }) {
  const history = useHistory()

  // Prepare our vaccinations
  const vaccinations = values?.registeredVaccinations || []

  // Save selected vaccination id and move to the next step
  const handleChange = (vaccinationId) => () => {
    onChange('vaccinationId', vaccinationId)
    changeStep()
  }

  // Helper to figure out if next booking is dose 3 (and its allowed start date)
  const allowDose3BookingFromDate = calculateAllowDose3BookingFromDate(vaccinations)
  const delayText = '20 uker'

  async function handleChangeStep() {
    onChange('vaccinationId', null)
    onChange('allowDose3BookingFromDate', allowDose3BookingFromDate)
    return changeStep()
  }

  // List of active vaccinations
  const activeVaccinations = filterActiveVaccinations(vaccinations)

  // List of future vaccinations
  const futureVaccinations = filterFutureVaccinations(vaccinations)

  const allowChangingBooking = orgUnit?.features?.includes('SELFSERVICE_REBOOKING') ?? false

  // Thou shall not pass!
  // (but you might if there is no future or active vaccinations - and you're not a hospital)
  const disallowNextStep = !!(
    futureVaccinations?.length ||
    activeVaccinations?.length ||
    orgUnit?.isHospital
  )

  const editExistingBooking = (vaccination) => {
    onChange('allowDose3BookingFromDate', allowDose3BookingFromDate)
    history.push(`/${orgUnit.slug}/endre-time/${bookingType.id}/${vaccination.vaccination_id}`)
  }

  return (
    <Step
      title={'Dine timer'}
      onSubmit={handleChangeStep}
      submit={disallowNextStep ? null : 'Bestill ny time'}
      submitDisabled={disallowNextStep}
    >
      <Editorial>
        {activeVaccinations?.length > 0 && (
          <>
            <h2>Klikk på timen du vil fylle ut egenerklæringen for:</h2>
            <br />
            <DisplayVaccination
              data={activeVaccinations}
              value={values?.vaccinationId}
              handleChange={handleChange}
              onClickEdit={allowChangingBooking && editExistingBooking}
            />
            <p>Ta kontakt med din kommune om du har behov for å endre eller slette din time.</p>
          </>
        )}

        {futureVaccinations?.length > 0 && (
          <>
            <h2>Fremtidige timer</h2>
            <DisplayVaccination
              data={futureVaccinations}
              value={values?.vaccinationId}
              onClickEdit={allowChangingBooking && editExistingBooking}
              disabled
            />

            <p>
              Egenerklæringen skal fylles ut nærmest mulig før timen, og tidligst tre dager før.
              Velkommen tilbake senere.
            </p>
          </>
        )}

        {allowDose3BookingFromDate && !disallowNextStep && (
          <>
            <p>
              Husk at det vanligvis skal gå <strong>minst {delayText}</strong> mellom vaksinasjon to
              og tre for din aldersgruppe.
              <br />
              <br />
              Vi viser deg derfor kun tilgjengelige datoer fra{' '}
              <strong>
                {formatDateClean(new Date(allowDose3BookingFromDate), 'd. MMMM yyyy')}
              </strong>{' '}
              når du skal bestille en ny time. Ta kontakt med ditt vaksinekontor dersom du tilhører
              en gruppe som skal ha et kortere intervall.
            </p>
          </>
        )}
      </Editorial>
    </Step>
  )
}

const DisplayVaccination = ({ data, value, disabled = false, handleChange, onClickEdit }) => {
  if (!data.length) {
    return null
  }

  return data.map((vaccination) => {
    const {
      location_title: location,
      vaccination_id: vaccinationId,
      start_time: startTime,
      completed_at: completedAt,
    } = vaccination

    // Show either booked date or completed date (ex: sysvak and drop-in doesn't have a booking/startTime)
    // We prefer completed date, since that might be different from the booking date
    const currentDate = completedAt ?? startTime
    if (!currentDate) {
      return null
    }

    return (
      <div key={currentDate}>
        <h3>{location}</h3>
        <Button
          style={{ marginTop: '-2px', marginBottom: '40px' }}
          disabled={disabled}
          small
          primary={value === vaccinationId}
          onClick={handleChange ? handleChange(vaccinationId) : () => {}}
        >
          {uppercaseFirst(formatDateClean(new Date(currentDate), "EEEE d. MMMM yyyy, 'kl.' HH:mm"))}
        </Button>{' '}
        {completedAt && (
          <div style={{ fontWeight: 'normal', marginTop: '-34px', marginBottom: '40px' }}>
            <Icon icon="check" style={{ color: '#53bc64', marginRight: '8px' }} /> Vaksinasjonen er
            registrert
          </div>
        )}
        {onClickEdit && (
          <button
            style={{ marginLeft: '15px', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault()
              onClickEdit(vaccination)
            }}
          >
            Endre time
          </button>
        )}
      </div>
    )
  })
}

Vaccinations.propTypes = {
  changeStep: T.func.isRequired,
  bookingType: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
    title: T.string.isRequired,
  }).isRequired,
  values: T.object.isRequired,
  onChange: T.func.isRequired,
  orgUnit: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
    isHospital: T.bool.isRequired,
    features: T.array.isRequired,
  }).isRequired,
}

DisplayVaccination.propTypes = {
  data: T.array.isRequired,
  disabled: T.bool,
  handleChange: T.func,
}
